import React from 'react';
import { LoadingButton } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { ChatConnection } from '../../../../../common/types';
import { useChatToolsActions, useChatToolsStore } from '../../../../../services/store/chat-tools';
import { messages } from './messages';

export const Action = ({ item }: { item: ChatConnection }) => {
	const {
		authorizeSlackConnection,
		delegateMSTConnection,
		authorizeMSTConnection,
		copyMSTAuthorizationLink,
	} = useChatToolsActions();
	const [{ loadingConnectionId }] = useChatToolsStore();

	const isLoading = loadingConnectionId === item.chatSpaceId;

	if (item.status === 'SITE_SETTING_CONFIGURED') {
		return (
			<>
				<CheckCircleIcon
					size="small"
					label=""
					primaryColor={token('color.icon.success', '#22A06B')}
				/>
				<FormattedMessage {...messages.chatToolsConnectTableConnectedTextNonFinal} />
			</>
		);
	}

	if (item.status === 'SITE_SETTING_NOT_PRESENT') {
		return (
			<LoadingButton
				onClick={() => {
					if (item.chatSpaceType === 'msteams') {
						delegateMSTConnection(item.chatSpaceId);
					} else if (item.chatSpaceType === 'slack') {
						authorizeSlackConnection(item.chatSpaceId);
					}
				}}
				isLoading={isLoading}
			>
				<FormattedMessage {...messages.chatToolsConnectTableConnectButtonTextNonFinal} />
			</LoadingButton>
		);
	}

	// msteams chat spaces
	if (item.status === 'REDUNDANT_TENANT' && item.chatSpaceType === 'msteams') {
		return (
			<Tooltip
				position="top"
				content={<FormattedMessage {...messages.redundantTenantTooltipNonFinal} />}
			>
				<Inline space="space.050" alignBlock="center" xcss={inlineStyles}>
					<FormattedMessage {...messages.redundantTenantTextNonFinal} />
				</Inline>
			</Tooltip>
		);
	}

	if (item.status === 'ADMIN_CONSENT_NOT_GRANTED' && item.chatSpaceType === 'msteams') {
		return (
			<DropdownMenu
				trigger={({ triggerRef, ...props }) => (
					<LoadingButton
						{...props}
						iconBefore={<ErrorIcon label="Error" size="small" />}
						iconAfter={<ChevronDown label="chevron-down" size="small" />}
						isLoading={isLoading}
						ref={triggerRef}
					>
						<FormattedMessage {...messages.givePermissionTextNonFinal} />
					</LoadingButton>
				)}
			>
				<DropdownItemGroup>
					<Tooltip
						position="top"
						content={<FormattedMessage {...messages.givePermissionTooltipNonFinal} />}
					>
						<DropdownItem onClick={() => authorizeMSTConnection(item.chatSpaceId)}>
							<FormattedMessage {...messages.givePermissionTextNonFinal} />
						</DropdownItem>
					</Tooltip>
					<Tooltip
						position="top"
						content={<FormattedMessage {...messages.copyLinkTooltipNonFinal} />}
					>
						<DropdownItem onClick={() => copyMSTAuthorizationLink(item.chatSpaceId)}>
							<FormattedMessage {...messages.copyLinkTextNonFinal} />
						</DropdownItem>
					</Tooltip>
				</DropdownItemGroup>
			</DropdownMenu>
		);
	}

	// It won't happen, there is a mistake on the BE side chatSpaceType is slack but status is not SITE_SETTING_NOT_PRESENT or SITE_SETTING_CONFIGURED
	return null;
};

const inlineStyles = xcss({
	cursor: 'not-allowed',
});
